import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import axios from 'axios';
import { CheckCircle, Coins, ArrowRight, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PurchaseSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshClient, loading: authLoading } = usePASClientAuth();
  const [confirming, setConfirming] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const purchaseId = searchParams.get('purchase_id');

  useEffect(() => {
    if (token && purchaseId) {
      confirmPurchase();
    } else if (!authLoading && !token) {
      navigate('/pas/login');
    }
  }, [token, purchaseId, authLoading]);

  const confirmPurchase = async () => {
    try {
      const response = await axios.post(
        `${API}/pas-auth/purchase/${purchaseId}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      await refreshClient();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to confirm purchase');
    } finally {
      setConfirming(false);
    }
  };

  if (confirming) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-white">Confirming your purchase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Purchase Issue</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/pas/dashboard')}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Purchase Complete!</h2>
        <p className="text-slate-400 mb-8">Your BETH has been added to your wallet</p>

        <div className="bg-slate-900 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Coins className="w-8 h-8 text-emerald-400" />
            <span className="text-3xl font-bold text-white">
              +{result?.beth_amount?.toLocaleString()} BETH
            </span>
          </div>
          <p className="text-slate-400">Added to your wallet</p>
          
          {result?.tx_hash && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-xs text-slate-500">Transaction Hash</p>
              <p className="text-emerald-400 font-mono text-xs break-all">{result.tx_hash}</p>
            </div>
          )}
        </div>

        <button
          onClick={() => navigate('/pas/dashboard')}
          className="w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-bold flex items-center justify-center gap-2"
        >
          Go to Dashboard <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default PurchaseSuccess;
